import { Injectable } from '@angular/core';
import { first, map, Observable, tap } from 'rxjs';
import { ColumnSorting } from 'src/app/core/models/common/column-sorting.model';
import { LeadInjector, leadInjectorSocialChannel } from 'src/app/core/models/lead/lead-injector.model';
import { LeadInjectorUpdateDto } from 'src/app/infrastructure/lobby-proxy/DTOs/lead-injector.dto';
import { LeadInjectorProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-lead-injector/lead-injector-proxy.service';
import { LeadInjectorDtoToLeadInjector } from 'src/app/shared/mappings/lead-model-dto/lead-injector.mapping';
import { throwIfLobbyErrorCode } from 'src/app/shared/utils/pipes/throwIfLobbyErrorCode.pipe';

@Injectable({
  providedIn: 'root'
})
export class LeadInjectorService {

  getLeadInjectors$(pageIndex: number = 0, description: string | null = null,
    nota: string | null = null, isActive: boolean | null = null,
    isEnabled: boolean | null = null,
    socialChannels: leadInjectorSocialChannel[] | null = null,
    sort: ColumnSorting | null = null
  ): Observable<LeadInjector[]> {

    return this.leadInjectorProxyService.getLeadInjectors$(pageIndex, 50, description, nota, isEnabled,
      null, null, isActive, socialChannels?.includes('facebook') ? true : null,
      this.getSortingKey(sort)).pipe(
        first(),
        throwIfLobbyErrorCode,
        map(dto => dto.data.map(injector => LeadInjectorDtoToLeadInjector(injector)!)),
        map(injectors => injectors.filter(i => i!.id! > 0))
      )
  }

  getLeadInjector$(id: number = 0): Observable<LeadInjector> {
    return this.leadInjectorProxyService.getLeadInjector$(id).pipe(
      first(),
      throwIfLobbyErrorCode,
      map(dto => LeadInjectorDtoToLeadInjector(dto.data)!)
    )
  }

  enableLeadInjector$(id: number, version: number): Observable<LeadInjector> {
    const dto = {
      isEnabled: true,
    } as LeadInjectorUpdateDto;

    return this.leadInjectorProxyService.patchLeadInjector$(id, dto, version).pipe(
      first(),
      throwIfLobbyErrorCode,
      map(dto => LeadInjectorDtoToLeadInjector(dto.data)!)
    )
  }

  disableLeadInjector$(id: number, version: number): Observable<LeadInjector> {
    const dto = {
      isEnabled: false,
    } as LeadInjectorUpdateDto;

    return this.leadInjectorProxyService.patchLeadInjector$(id, dto, version).pipe(
      first(),
      throwIfLobbyErrorCode,
      map(dto => LeadInjectorDtoToLeadInjector(dto.data)!)
    )
  }

  constructor(private readonly leadInjectorProxyService: LeadInjectorProxyService) { }


  private getSortingKey(sort: ColumnSorting | null): 'Id' | 'Enabled' | 'ActivationBegin' | 'ActivationEnd' | 'Updated' | 'Created' | 'Id-' | 'Enabled-' | 'ActivationBegin-' | 'ActivationEnd-' | 'Updated-' | 'Created-' | null {
    if (!sort) return null

    switch ([sort.columnName, sort.sortingDirection].join(',')) {
      case 'id,up': return 'Id';
      case 'enabled,up': return 'Enabled';
      case 'activeFrom,up': return 'ActivationBegin';
      case 'activeTo,up': return 'ActivationEnd';
      case 'updated,up': return 'Updated';
      case 'created,up': return 'Created';

      case 'id,down': return 'Id-';
      case 'enabled,down': return 'Enabled-';
      case 'activeFrom,down': return 'ActivationBegin-';
      case 'activeTo,down': return 'ActivationEnd-';
      case 'updated,down': return 'Updated-';
      case 'created,down': return 'Created-';

      default: return null
    }
  }
}
